import React, { useState, useEffect } from "react"; 
import api from "../../../services/api";
import "./CSVDownloadPage.css";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

const COLUMN_TYPE = "COLUMN";

const DEFAULT_COLUMNS = [
  { key: "receipt_number", label: "№ квитанции", visible: true },
  { key: "contract_number", label: "№ договора", visible: true },
  { key: "time", label: "Время", visible: true },
  { key: "delivery_time", label: "Время доставки", visible: true },
  { key: "delivery_address", label: "Адрес доставки", visible: true },
  { key: "client_name", label: "Клиент", visible: true },
  { key: "amount", label: "Сумма", visible: true },
  { key: "to_pay", label: "К оплате", visible: true },
  { key: "paid", label: "Оплачено", visible: true },
  { key: "payment_type", label: "Тип оплаты", visible: true },
  { key: "status", label: "Статус", visible: true },
  { key: "products", label: "Товары", visible: true },
];

const STATUS_COLORS = {
  "Открыта": "#007bff",
  "Закрыта": "#28a745",
  "Не доставлено": "#dc3545",
  "В обработке": "#ffc107",
};

const DispatcherRoutes = () => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [columns, setColumns] = useState([]);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [filterPoschetu, setFilterPoschetu] = useState(false); // фильтр по типу оплаты "ПоСчету"

  const allReceipts = routes.flatMap(route => route.receipts || []);
  const organizations = Array.from(
    new Set(allReceipts.map(receipt => receipt.organization).filter(Boolean))
  );



  // Загрузка колонок из localStorage
  useEffect(() => {
    try {
      const savedColumns = JSON.parse(localStorage.getItem("dispatcherColumns"));
      if (savedColumns && Array.isArray(savedColumns) && savedColumns.length > 0) {
        setColumns(savedColumns);
      } else {
        throw new Error("Ошибка данных в localStorage, загружаем стандартные колонки.");
      }
    } catch (error) {
      console.error(error.message);
      setColumns(DEFAULT_COLUMNS);
      localStorage.setItem("dispatcherColumns", JSON.stringify(DEFAULT_COLUMNS));
    }
  }, []);

  useEffect(() => {
    if (columns.length > 0) {
      localStorage.setItem("dispatcherColumns", JSON.stringify(columns));
    }
  }, [columns]);

  // Загрузка маршрутов с учетом диапазона дат
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await api.get("/routes/by-date", {
          params: {
            startDate: startDate.toISOString().split("T")[0],
            endDate: endDate.toISOString().split("T")[0],
          },
        });
        setRoutes(response || []);
      } catch (err) {
        console.error("Ошибка загрузки маршрутов", err);
      }
    };
    fetchRoutes();
  }, [startDate, endDate]);

  // Фильтрация квитанций выбранного маршрута с учетом поискового запроса и чекбокса "ПоСчету"
  const filteredReceipts = selectedRoute?.receipts.filter((receipt) => {
    const matchesSearch = Object.values(receipt)
      .filter((value) => typeof value === "string" || typeof value === "number")
      .some((value) => value.toString().toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesPayment = !filterPoschetu || receipt.payment_type === "ПоСчету";
    return matchesSearch && matchesPayment;
  }) || [];

  // Сортировка квитанций
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    if (!sortColumn) return 0;
    let valueA = a[sortColumn] ?? "";
    let valueB = b[sortColumn] ?? "";
    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();
    if (sortOrder === "asc") return valueA > valueB ? 1 : -1;
    if (sortOrder === "desc") return valueA < valueB ? 1 : -1;
    return 0;
  });

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortOrder === "asc") setSortOrder("desc");
      else if (sortOrder === "desc") setSortOrder(null);
      else setSortOrder("asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const toggleColumnVisibility = (key) => {
    setColumns((prev) =>
      prev.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col))
    );
  };

  const moveColumn = (dragIndex, hoverIndex) => {
    setColumns((prevColumns) => {
      const visibleColumns = prevColumns.filter(col => col.visible);
      const draggedColKey = visibleColumns[dragIndex].key;
      const hoverColKey = visibleColumns[hoverIndex].key;
      const realDragIndex = prevColumns.findIndex(col => col.key === draggedColKey);
      const realHoverIndex = prevColumns.findIndex(col => col.key === hoverColKey);
      const updatedColumns = [...prevColumns];
      const [movedColumn] = updatedColumns.splice(realDragIndex, 1);
      updatedColumns.splice(realHoverIndex, 0, movedColumn);
      localStorage.setItem("dispatcherColumns", JSON.stringify(updatedColumns));
      return updatedColumns;
    });
  };

  const Column = ({ index, column }) => {
    const ref = React.useRef(null);
    const [{ isDragging }, drag] = useDrag({
      type: COLUMN_TYPE,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });
    const [, drop] = useDrop({
      accept: COLUMN_TYPE,
      hover: (draggedItem, monitor) => {
        const dragIndex = draggedItem.index;
        const hoverIndex = index; 
        if (dragIndex === hoverIndex) return;
        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        if (!hoverBoundingRect) return;
        const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
        const mouseX = monitor.getClientOffset().x - hoverBoundingRect.left;
        if (dragIndex < hoverIndex && mouseX < hoverMiddleX) return;
        if (dragIndex > hoverIndex && mouseX > hoverMiddleX) return;
        moveColumn(dragIndex, hoverIndex);
        draggedItem.index = hoverIndex;
      },
    });
    drag(drop(ref));
    return (
      <th
        ref={ref}
        style={{
          cursor: "grab",
          opacity: isDragging ? 0.5 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
        onClick={() => handleSort(column.key)}
      >
        {column.label}{" "}
        {sortColumn === column.key
          ? sortOrder === "asc"
            ? "▲"
            : sortOrder === "desc"
            ? "▼"
            : ""
          : ""}
      </th>
    );
  };

  // Подсчет отсканированных марок по товарам квитанции
  const getMarksInfo = (receipt) => {
    let totalExpected = 0;
    let scannedCount = 0;
    receipt.products.forEach((product) => {
      if (product.is_marked) {
        totalExpected += product.actual_quantity;
        scannedCount += product.marks ? product.marks.length : 0;
      }
    });
    return { scannedCount, totalExpected };
  };

  const escapeCSV = (str) => {
    if (typeof str !== "string") {
      return str;
    }
    return str.replace(/"/g, '""');
  };

  // Генерация CSV для квитанции.
  // Для каждого товара с маркировкой формируется одна строка:
  // Номер строки,,,,,,КИЗ,«mark1»,"mark2",...
  const generateCSVForReceipt = (receipt) => {
    let csv = "";
    let lineNumber = 1;
    
    receipt.products.forEach((product) => {
      if (product.is_marked) {
        const marksArray = product.marks && product.marks.length > 0 ? product.marks : [];
        
        // Удаляем невидимые символы, включая GS (\u001D)
        const cleanMarks = marksArray.map(mark => mark.replace(/[\u200B-\u200D\uFEFF\u001D]/g, '').slice(0, -6));
        
        const marksCsv = cleanMarks.map(mark => `"${escapeCSV(mark)}"`).join(",");
        csv += `${lineNumber},,,,,,КИЗ`;
        if (marksCsv) {
          csv += `,${marksCsv}`;
        }
        csv += "\n";
        lineNumber++;
      }
    });
  
    return csv;
  };

  const generateCSVForOrganizationAll = (organization) => {
    let csv = "";
    let lineNumber = 1;
    const productGroups = {};
  
    // Проходим по всем квитанциям выбранной организации
    allReceipts
      .filter((receipt) => receipt.organization === organization)
      .forEach((receipt) => {
        receipt.products.forEach((product) => {
          if (product.is_marked) {
            // Группируем товары по виду (например, по product_name)
            if (!productGroups[product.product_name]) {
              productGroups[product.product_name] = [];
            }
            const marksArray =
              product.marks && product.marks.length > 0 ? product.marks : [];
            // Очищаем маркировки от невидимых символов и обрезаем последние 6 символов
            const cleanMarks = marksArray.map((mark) =>
              mark.replace(/[\u200B-\u200D\uFEFF\u001D]/g, "").slice(0, -6)
            );
            // Добавляем маркировки в соответствующую группу
            productGroups[product.product_name].push(...cleanMarks);
          }
        });
      });
  
    // Для каждого вида товара формируем одну строку CSV:
    // Номер строки,,,,,,КИЗ, маркировка всех товаров данного вида
    Object.keys(productGroups).forEach((productType) => {
      const marksCsv = productGroups[productType]
        .map((mark) => `"${escapeCSV(mark)}"`)
        .join(",");
      csv += `${lineNumber},,,,,,КИЗ,${marksCsv}\n`;
      lineNumber++;
    });
  
    return csv;
  };
  
  
  
  const downloadCSVForOrganizationAll = (organization) => {
    const csvContent = generateCSVForOrganizationAll(organization);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const fileName = `marks_${organization}_${startDate.toISOString().split("T")[0]}-${endDate.toISOString().split("T")[0]}.csv`
      .replace(/[\s"]/g, "_");
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const generateWatermarkCSVForOrganization = (organization) => {
    let watermarkCSV = "";
    const CRLF = "\r\n";
    const GS = String.fromCharCode(29);

    allReceipts
      .filter((receipt) => receipt.organization === organization)
      .forEach((receipt) => {
        receipt.products.forEach((product) => {
          if (product.is_marked) {
            const marksArray = product.marks && product.marks.length > 0 ? product.marks : [];
            marksArray.forEach((mark) => {
              // Убираем ведущий GS, если есть
              let cleanedMark = mark;
              if (cleanedMark.charCodeAt(0) === 29) {
                cleanedMark = cleanedMark.slice(1);
              }
              watermarkCSV += cleanedMark + CRLF;
            });
          }
        });
      });

    return watermarkCSV;
  };


  const downloadWatermarkCSVForOrganization = (organization) => {
    const watermarkCSV = generateWatermarkCSVForOrganization(organization);
    const blob = new Blob([watermarkCSV], { type: "text/csv;charset=utf-8;" });
    const fileName = `watermark_${organization}_${startDate.toISOString().split("T")[0]}-${endDate.toISOString().split("T")[0]}.csv`
      .replace(/[\s"]/g, "_");
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  

  // Функция для скачивания CSV для квитанции
  const downloadCSV = (receipt) => {
    const csvContent = generateCSVForReceipt(receipt);
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const fileName = `${receipt.receipt_number}-${receipt.contract_number}-${receipt.client_name}.csv`
      .replace(/[\s"]/g, "_");
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="csvdownliad-dispatcher-container">
        <aside className="csvdownliad-sidebar">
          <div className="csvdownliad-date-filter-row">
            <label>

              <input
                type="date"
                value={isNaN(startDate.getTime()) ? '' : startDate.toISOString().split("T")[0]}
                onChange={(e) => setStartDate(new Date(e.target.value))}
              />
            </label>
            <label>
            <input
              type="date"
              value={isNaN(endDate.getTime()) ? '' : endDate.toISOString().split("T")[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
            />

            </label>
          </div>
          <div className="csvdownliad-filter-checkbox">
            <label>
              <input
                type="checkbox"
                checked={filterPoschetu}
                onChange={(e) => setFilterPoschetu(e.target.checked)}
              />
              Показать только "ПоСчету"
            </label>
            {organizations.length > 0 && (
              <div className="organization-downloads">
                <h3>Скачать марки по организациям (все маршрутные листы)</h3>
                {organizations.map((org) => (
                  <div key={org} style={{ marginBottom: '10px' }}>
                    <button 
                      className="csv-btn"
                      onClick={() => downloadCSVForOrganizationAll(org)}
                    >
                      Скачать CSV для {org}
                    </button>
                    <button 
                      className="watermark-btn"
                      onClick={() => downloadWatermarkCSVForOrganization(org)}
                    >
                      Скачать Watermark CSV для {org}
                    </button>
                  </div>
                ))}
              </div>
            )}

          </div>
          <ul className="csvdownliad-routes-list">
            {routes.map((route) => (
              <li key={route.id} onClick={() => setSelectedRoute(route)}>
                Маршрут №{route.route_number} Водитель: {route.driver} Районы: {route.route_districts} ({route.route_date})
              </li>
            ))}
          </ul>
        </aside>

        <main className="csvdownliad-route-details">
          <h3>Настройка колонок</h3>
          {columns.map((col) => (
            <label key={col.key}>
              <input
                type="checkbox"
                checked={col.visible}
                onChange={() => toggleColumnVisibility(col.key)}
              />
              {col.label}
            </label>
          ))}
          {selectedRoute ? (
            <>
              <h2>Маршрут №{selectedRoute.route_number}</h2>
              <h3>Маршрут №{selectedRoute.route_number} Водитель {selectedRoute.driver}</h3>
              <h3>Районы: {selectedRoute.route_districts}</h3>

              <input
                type="text"
                className="csvdownliad-search-input2"
                placeholder="Введите текст для поиска..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <table className="csvdownliad-receipts-table">
                <thead>
                  <tr>
                    {columns.filter(col => col.visible).map((col, index) => (
                      <Column key={col.key} index={index} column={col} />
                    ))}
                    <th>Марки / CSV</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedReceipts.length > 0 ? (
                    sortedReceipts.map((receipt) => {
                      const { scannedCount, totalExpected } = getMarksInfo(receipt);
                      return (
                        <tr key={receipt.id}>
                          {columns.filter((col) => col.visible).map((col) => (
                            <td key={col.key} style={col.key === "status" ? { backgroundColor: STATUS_COLORS[receipt[col.key]], color: "white", fontWeight: "bold", padding: "5px", borderRadius: "5px" } : {}}>
                              {col.key === "products"
                                ? receipt.products.map((p) => `${p.product_name} x${p.actual_quantity}`).join(", ")
                                : receipt[col.key]}
                            </td>
                          ))}
                          <td>
                            <div>{`Сканировано: ${scannedCount}/${totalExpected}`}</div>
                            <button onClick={() => downloadCSV(receipt)}>Скачать CSV</button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={columns.filter((col) => col.visible).length + 1} className="csvdownliad-no-results">
                        Нет квитанций, соответствующих поиску.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <p>Выберите маршрут для просмотра информации</p>
          )}
        </main>
      </div>
    </DndProvider>
  );
};

export default DispatcherRoutes;
